import { useState } from 'react';
import { Plus, Pencil, Trash2, Eye, EyeOff, Newspaper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from './constants';

export default function NewsTab({ posts, onRefresh }) {
  const [editingPost, setEditingPost] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ 
    title: '', 
    content: '', 
    image_url: '', 
    link_url: '',
    is_published: true 
  });

  const openDialog = (post = null) => {
    if (post) {
      setEditingPost(post);
      setForm({ 
        title: post.title, 
        content: post.content, 
        image_url: post.image_url || '',
        link_url: post.link_url || '',
        is_published: post.is_published !== false
      });
    } else {
      setEditingPost(null);
      setForm({ title: '', content: '', image_url: '', link_url: '', is_published: true });
    }
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.title || !form.content) {
      toast.error('Please fill in title and content');
      return;
    }
    try {
      if (editingPost) {
        await axios.put(`${API}/admin/news/${editingPost.id}`, form);
        toast.success('News post updated');
      } else {
        await axios.post(`${API}/admin/news`, form);
        toast.success('News post created');
      }
      setShowDialog(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to save post');
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this news post?')) return;
    try {
      await axios.delete(`${API}/admin/news/${id}`);
      toast.success('Post deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const togglePublished = async (post) => {
    try {
      await axios.put(`${API}/admin/news/${post.id}`, { 
        ...post, 
        is_published: !post.is_published 
      });
      toast.success(post.is_published ? 'Post unpublished' : 'Post published');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Newspaper className="w-5 h-5" />
          News Posts ({posts?.length || 0})
        </CardTitle>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog()} className="gap-2">
              <Plus className="w-4 h-4" /> Add News Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingPost ? 'Edit News Post' : 'Add News Post'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input 
                  value={form.title} 
                  onChange={(e) => setForm({ ...form, title: e.target.value })} 
                  placeholder="Breaking: Something Nobody Cares About..."
                />
              </div>
              <div>
                <Label>Content *</Label>
                <Textarea 
                  rows={4} 
                  value={form.content} 
                  onChange={(e) => setForm({ ...form, content: e.target.value })} 
                  placeholder="The news content..."
                />
              </div>
              <div>
                <Label>Image URL (optional)</Label>
                <Input 
                  value={form.image_url} 
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })} 
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>Link URL (optional)</Label>
                <Input 
                  value={form.link_url} 
                  onChange={(e) => setForm({ ...form, link_url: e.target.value })} 
                  placeholder="https://youtube.com/..."
                />
              </div>
              <Button onClick={save} className="w-full">{editingPost ? 'Update' : 'Create'} Post</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {posts?.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No news posts yet. Add your first post!</p>
        ) : (
          <div className="space-y-4">
            {posts?.map((post) => (
              <div 
                key={post.id} 
                className={`p-4 rounded-lg ${post.is_published ? 'bg-muted/50' : 'bg-muted/20 border border-dashed'}`}
              >
                <div className="flex items-start gap-4">
                  {post.image_url && (
                    <img 
                      src={post.image_url} 
                      alt={post.title} 
                      className="w-20 h-20 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="font-bold">{post.title}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{post.content}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${post.is_published ? 'bg-green-500/20 text-green-500' : 'bg-muted text-muted-foreground'}`}>
                        {post.is_published ? 'Published' : 'Draft'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(post.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => togglePublished(post)}>
                      {post.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openDialog(post)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => deleteItem(post.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
